(function () {
  'use strict';

  let subtotalObserver = null;
  let footerObserver = null;
  let cartUpdateUnsubscriber = null;
  let updateTimeout = null;
  let lastSubtotalValue = null;
  let pollingInterval = null;
  let animationFrame = null;
  let currentProgress = 0;

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // NC-CRO 2026-10: toggle the unlocked state on the bar and the footer
  // ("Shipping: Free" row vs "shipping calculated at checkout" note).
  function setFreeShippingUnlocked(container, unlocked) {
    if (container) container.classList.toggle('is-unlocked', unlocked);
    document.querySelectorAll('#CartDrawer [data-nc-ship-free]').forEach(function (el) {
      el.hidden = !unlocked;
    });
    document.querySelectorAll('#CartDrawer [data-nc-ship-note]').forEach(function (el) {
      el.hidden = unlocked;
    });
  }

  function animateProgressBar(progressFill, targetPercentage, container) {
    if (animationFrame) cancelAnimationFrame(animationFrame);

    // Pick up the server-rendered width so the first update doesn't animate
    // from 0 every time the drawer markup is (re)rendered.
    if (currentProgress === 0) {
      const inlineWidth = parseFloat(progressFill.style.width);
      if (!isNaN(inlineWidth) && inlineWidth > 0) currentProgress = Math.min(inlineWidth, 100);
    }

    const startProgress = currentProgress;
    const targetProgress = targetPercentage;
    const duration = 600;
    const startTime = performance.now();

    function animate(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      currentProgress = startProgress + (targetProgress - startProgress) * easeOutCubic;
      progressFill.style.width = currentProgress + '%';
      // Safety net for the theme's global ":empty { display: none }" rule;
      // the primary fix lives in custom-cart-drawer.css on the fill selector.
      progressFill.style.display = 'block';

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        currentProgress = targetProgress;
        animationFrame = null;
      }
    }

    animationFrame = requestAnimationFrame(animate);
  }

  function parsePriceToCents(priceText) {
    if (!priceText) return 0;

    let cleanedText = String(priceText)
      .replace(/[\s€$£¥₹]/g, '')
      .replace(/[^\d,.-]/g, '')
      .trim();

    if (!cleanedText) return 0;

    const hasComma = cleanedText.includes(',');
    const hasDot = cleanedText.includes('.');

    if (hasComma && hasDot) {
      const lastCommaIndex = cleanedText.lastIndexOf(',');
      const lastDotIndex = cleanedText.lastIndexOf('.');

      if (lastCommaIndex > lastDotIndex) {
        const parts = cleanedText.split(',');
        if (parts.length === 2) {
          const integerPart = parts[0].replace(/\./g, '');
          const decimalPart = parts[1];
          if (/^\d+$/.test(integerPart) && /^\d+$/.test(decimalPart) && decimalPart.length <= 2) {
            const integerValue = parseInt(integerPart, 10);
            const decimalValue = parseInt(decimalPart, 10);
            if (!isNaN(integerValue) && !isNaN(decimalValue)) return integerValue * 100 + decimalValue;
          }
        }
      } else if (lastDotIndex > lastCommaIndex) {
        const dotIndex = cleanedText.lastIndexOf('.');
        const integerPart = cleanedText.substring(0, dotIndex).replace(/,/g, '');
        const decimalPart = cleanedText.substring(dotIndex + 1);
        if (decimalPart.length <= 2 && /^\d+$/.test(integerPart) && /^\d+$/.test(decimalPart)) {
          const integerValue = parseInt(integerPart, 10);
          const decimalValue = parseInt(decimalPart, 10);
          if (!isNaN(integerValue) && !isNaN(decimalValue)) return integerValue * 100 + decimalValue;
        }
      }
    } else if (hasComma && !hasDot) {
      const parts = cleanedText.split(',');
      if (parts.length === 2) {
        const integerPart = parts[0];
        const decimalPart = parts[1];
        if (/^\d+$/.test(integerPart) && /^\d+$/.test(decimalPart) && decimalPart.length <= 2) {
          const integerValue = parseInt(integerPart, 10);
          const decimalValue = parseInt(decimalPart, 10);
          if (!isNaN(integerValue) && !isNaN(decimalValue)) return integerValue * 100 + decimalValue;
        }
      }
    } else if (hasDot && !hasComma) {
      const parts = cleanedText.split('.');
      if (parts.length >= 2 && parts[parts.length - 1].length <= 2) {
        const decimalPart = parts[parts.length - 1];
        const integerPart = parts.slice(0, -1).join('').replace(/\./g, '');
        if (/^\d+$/.test(integerPart) && /^\d+$/.test(decimalPart)) {
          const integerValue = parseInt(integerPart, 10);
          const decimalValue = parseInt(decimalPart, 10);
          if (!isNaN(integerValue) && !isNaN(decimalValue)) return integerValue * 100 + decimalValue;
        }
      } else {
        const integerPart = cleanedText.replace(/\./g, '');
        if (/^\d+$/.test(integerPart)) {
          const integerValue = parseInt(integerPart, 10);
          if (!isNaN(integerValue)) return integerValue * 100;
        }
      }
    } else {
      if (/^\d+$/.test(cleanedText)) {
        const integerValue = parseInt(cleanedText, 10);
        if (!isNaN(integerValue)) return integerValue * 100;
      }
    }

    const digitsOnly = cleanedText.replace(/[^\d]/g, '');
    if (digitsOnly.length > 0) {
      const lastChar = cleanedText[cleanedText.length - 1];
      const secondLastChar = cleanedText[cleanedText.length - 2];
      if (
        /^\d$/.test(lastChar) &&
        /^\d$/.test(secondLastChar) &&
        (cleanedText[cleanedText.length - 3] === ',' || cleanedText[cleanedText.length - 3] === '.')
      ) {
        const integerDigits = digitsOnly.slice(0, -2);
        const decimalDigits = digitsOnly.slice(-2);
        if (integerDigits.length > 0) {
          const integerValue = parseInt(integerDigits, 10);
          const decimalValue = parseInt(decimalDigits, 10);
          if (!isNaN(integerValue) && !isNaN(decimalValue)) return integerValue * 100 + decimalValue;
        }
      }
      const fallbackValue = parseInt(digitsOnly, 10);
      if (!isNaN(fallbackValue) && fallbackValue > 0)
        return fallbackValue >= 1000 ? fallbackValue : fallbackValue * 100;
    }

    return 0;
  }

  // 2026-10-19: the hidden #cartsubTotal carries the exact subtotal in cents
  // (presentment currency) in data-cents; parsing the formatted text is only a
  // fallback (it misreads formats like "1.268,00 kr.").
  function readSubtotalCents() {
    const el = document.getElementById('cartsubTotal');
    if (!el) return null;
    const raw = el.getAttribute('data-cents');
    if (raw !== null && raw !== '' && /^\d+$/.test(raw)) return parseInt(raw, 10);
    return parsePriceToCents(el.textContent || el.innerText);
  }

  // 2026-10-19: free-shipping thresholds are EUR (shop currency) cents. When the
  // cart is in another currency (RON, DKK, PLN…) Liquid renders the bar hidden
  // with data-nc-fs-convert; here the threshold is converted with
  // Shopify.currency.rate (presentment units per 1 EUR). Returns 0 (= feature
  // off, bar stays hidden, "shipping at checkout") when no usable rate exists.
  function getLocalThreshold(container, eurThreshold) {
    if (!container.hasAttribute('data-nc-fs-convert')) return eurThreshold;
    const cur = (window.Shopify && Shopify.currency) || null;
    const want = container.getAttribute('data-nc-fs-currency') || '';
    if (!cur || !want || cur.active !== want) return 0;
    const rate = parseFloat(cur.rate);
    if (!isFinite(rate) || rate <= 0) return 0;
    // ceil: never promise free shipping below the real (converted) threshold
    return Math.ceil(eurThreshold * rate);
  }

  // Formats cents of the cart currency, rounded UP to whole units, using the
  // pattern of the store's own money output for that currency (Liquid renders
  // 12345.67 as data-nc-fs-money-sample, e.g. "12.345,67 lei", "12.345,67 kr.",
  // "12 345,67 zł"). Falls back to Intl.NumberFormat.
  function formatLocalWhole(cents, container) {
    const units = Math.ceil(cents / 100);
    const sample = container.getAttribute('data-nc-fs-money-sample') || '';
    const m = sample.match(/\d[\d.,'   ]*\d/);
    if (m) {
      const num = m[0];
      const prefix = sample.slice(0, m.index);
      const suffix = sample.slice(m.index + num.length);
      // decimals present when the sample ends in a separator + 2 digits
      const dec = /[.,]\d{2}$/.test(num) ? num.charAt(num.length - 3) : '';
      const intPart = dec ? num.slice(0, -3) : num;
      const sepMatch = intPart.match(/[^\d]/);
      const thousands = sepMatch ? sepMatch[0] : '';
      const grouped = thousands ? String(units).replace(/\B(?=(\d{3})+(?!\d))/g, thousands) : String(units);
      return (prefix + grouped + suffix).trim();
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: container.getAttribute('data-nc-fs-currency') || 'EUR',
        maximumFractionDigits: 0
      }).format(units);
    } catch (e) {
      return String(units);
    }
  }

  function updateFreeShippingProgress(subtotalCents) {
    const freeShippingContainer = document.querySelector('.cart-drawer__free-shipping');
    if (!freeShippingContainer) return;

    // Threshold is configured in Theme Settings and rendered into the data
    // attribute by Liquid; if it is missing or invalid the feature is off.
    const eurThreshold = parseInt(freeShippingContainer.dataset.freeShippingThreshold, 10);
    if (!eurThreshold || eurThreshold <= 0) return;
    const isConverted = freeShippingContainer.hasAttribute('data-nc-fs-convert');
    const freeShippingThreshold = getLocalThreshold(freeShippingContainer, eurThreshold);
    if (!freeShippingThreshold || freeShippingThreshold <= 0) {
      // local currency without a usable rate: keep the bar hidden, never claim "free"
      freeShippingContainer.hidden = true;
      setFreeShippingUnlocked(freeShippingContainer, false);
      return;
    }

    if (typeof subtotalCents === 'number' && !isNaN(subtotalCents) && subtotalCents >= 0) {
      subtotalCents = Math.floor(subtotalCents);
    } else if (typeof subtotalCents === 'string') {
      const parsed = parseFloat(subtotalCents);
      subtotalCents = !isNaN(parsed) && parsed >= 0 ? Math.floor(parsed) : parsePriceToCents(subtotalCents);
    } else {
      const fromDom = readSubtotalCents();
      subtotalCents = fromDom === null ? 0 : fromDom;
    }

    if (isNaN(subtotalCents) || subtotalCents < 0) subtotalCents = 0;
    subtotalCents = Math.floor(subtotalCents);
    // A freshly re-rendered converted bar (no data-nc-fs-done yet) must be filled
    // even when the subtotal did not change.
    const isFilled = !isConverted || freeShippingContainer.getAttribute('data-nc-fs-done') === String(subtotalCents);
    if (subtotalCents === lastSubtotalValue && isFilled) return;
    lastSubtotalValue = subtotalCents;
    if (isConverted) {
      freeShippingContainer.setAttribute('data-nc-fs-done', String(subtotalCents));
      const goalEl = freeShippingContainer.querySelector('.cart-drawer__free-shipping-goal');
      if (goalEl) goalEl.textContent = formatLocalWhole(freeShippingThreshold, freeShippingContainer);
      freeShippingContainer.hidden = false;
    }

    if (subtotalCents > 0) freeShippingContainer.dataset.initialSubtotal = String(subtotalCents);

    let progressPercentage = 0;
    if (subtotalCents > 0 && freeShippingThreshold > 0)
      progressPercentage = Math.min((subtotalCents / freeShippingThreshold) * 100, 100);
    progressPercentage = Math.max(0, Math.min(100, progressPercentage || 0));

    const progressFill = freeShippingContainer.querySelector('.cart-drawer__free-shipping-progress-fill');
    if (progressFill) animateProgressBar(progressFill, progressPercentage, freeShippingContainer);

    setFreeShippingUnlocked(freeShippingContainer, progressPercentage >= 100);

    const statusElement = freeShippingContainer.querySelector('.cart-drawer__free-shipping-status');
    const statusTextSpan = freeShippingContainer.querySelector('.cart-drawer__free-shipping-status-text');
    if (statusElement && statusTextSpan) {
      if (progressPercentage >= 100) {
        statusTextSpan.textContent = statusElement.getAttribute('data-translation-unlocked') || '';
      } else {
        const remainingCents = freeShippingThreshold - subtotalCents;
        if (remainingCents > 0) {
          // Prefer the store's own money format; fall back to hand-rolled
          // es-ES euro formatting. Trailing ",00"/".00" is stripped to match
          // Liquid's money_without_trailing_zeros used for the initial render.
          let amount;
          if (isConverted) {
            // Shopify.formatMoney here is hardcoded to "…€": use the cart currency's own format
            amount = formatLocalWhole(remainingCents, freeShippingContainer);
          } else if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
            amount = String(Shopify.formatMoney(remainingCents)).replace(/([.,]00)(?=\s|€|$)/, '').trim();
          } else {
            const remainingValue = remainingCents / 100;
            const parts = remainingValue.toFixed(2).split('.');
            const integerPart = parts[0];
            const decimalPart = parts[1] || '00';
            const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            const formattedValue = decimalPart === '00' ? formattedInteger : formattedInteger + ',' + decimalPart;
            amount = formattedValue + '€';
          }

          let translationTemplate = statusElement.getAttribute('data-translation') || '';
          if (!translationTemplate) translationTemplate = statusTextSpan.textContent || translationTemplate;

          // The amount is shown in bold: escape the template, then insert <strong>.
          const amountHtml = '<strong>' + escapeHtml(amount) + '</strong>';
          let translatedHtml = escapeHtml(translationTemplate).replace(/__AMOUNT__/gi, amountHtml);
          translatedHtml = translatedHtml.replace(/\{\{\s*amount\s*\}\}/gi, amountHtml);

          statusTextSpan.innerHTML = translatedHtml;
        }
      }
    }
  }

  // true while a re-rendered local-currency bar has not been filled in yet
  function needsConvertFill() {
    const c = document.querySelector('.cart-drawer__free-shipping[data-nc-fs-convert]');
    return !!(c && !c.hasAttribute('data-nc-fs-done'));
  }

  function forceUpdate() {
    clearTimeout(updateTimeout);
    updateTimeout = setTimeout(function () {
      const currentValue = readSubtotalCents();
      if (currentValue !== null) {
        if (currentValue !== lastSubtotalValue || needsConvertFill()) {
          updateFreeShippingProgress();
          setupSubtotalObserver();
        }
      } else {
        updateFreeShippingProgress();
      }
    }, 50);
  }

  function setupSubtotalObserver() {
    const subtotalElement = document.getElementById('cartsubTotal');
    if (!subtotalElement) return;

    if (subtotalObserver) subtotalObserver.disconnect();
    let observerTimeout;
    subtotalObserver = new MutationObserver(function () {
      clearTimeout(observerTimeout);
      observerTimeout = setTimeout(function () {
        updateFreeShippingProgress();
      }, 50);
    });

    subtotalObserver.observe(subtotalElement, { childList: true, characterData: true, subtree: true });
  }

  function setupFooterObserver() {
    const footer = document.querySelector('.drawer__footer');
    if (!footer) return;

    if (footerObserver) footerObserver.disconnect();
    footerObserver = new MutationObserver(function () {
      setTimeout(forceUpdate, 100);
    });

    footerObserver.observe(footer, { childList: true, subtree: true, characterData: true });
  }

  function startPolling() {
    if (pollingInterval) clearInterval(pollingInterval);
    pollingInterval = setInterval(function () {
      const currentValue = readSubtotalCents();
      if (currentValue !== null && (currentValue !== lastSubtotalValue || needsConvertFill())) updateFreeShippingProgress();
    }, 500);
  }

  function stopPolling() {
    if (pollingInterval) clearInterval(pollingInterval);
    pollingInterval = null;
  }

  function handleCartUpdate(event) {
    lastSubtotalValue = null;
    if (event && event.items_subtotal_price !== undefined) {
      updateFreeShippingProgress(event.items_subtotal_price);
    } else {
      setTimeout(forceUpdate, 150);
    }
    setTimeout(function () {
      setupSubtotalObserver();
      setupFooterObserver();
    }, 100);
  }

  function initUpsellsCarousel() {
    const track = document.getElementById('upsells-track');
    const carousel = track ? track.closest('.cart-drawer__upsells-carousel') : null;
    if (!track || !carousel) return;

    // Drag-to-scroll functionality
    let isDragging = false;
    let startX = 0;
    let scrollLeft = 0;
    let velocity = 0;
    let lastX = 0;
    let lastTime = 0;
    let animationId = null;

    if (!carousel.hasAttribute('data-drag-listener-attached')) {
      carousel.setAttribute('data-drag-listener-attached', 'true');
      carousel.style.cursor = 'grab';
      carousel.style.userSelect = 'none';

      // Track whether the pointer actually moved enough to count as a drag
      let hasDragged = false;
      const DRAG_THRESHOLD = 5; // px — movement below this is treated as a click

      const handleDragStart = function (e) {
        isDragging = true;
        hasDragged = false;
        carousel.style.cursor = 'grabbing';
        startX = (e.pageX || e.touches?.[0]?.pageX || 0) - carousel.offsetLeft;
        scrollLeft = carousel.scrollLeft;
        velocity = 0;
        lastX = e.pageX || e.touches?.[0]?.pageX || 0;
        lastTime = Date.now();

        if (animationId) {
          cancelAnimationFrame(animationId);
          animationId = null;
        }
      };

      const handleDragMove = function (e) {
        if (!isDragging) return;

        const x = (e.pageX || e.touches?.[0]?.pageX || 0) - carousel.offsetLeft;
        const walk = (x - startX) * 1.5; // Scroll speed multiplier

        // Only start dragging visually after exceeding threshold
        if (!hasDragged && Math.abs(x - startX) > DRAG_THRESHOLD) {
          hasDragged = true;
          carousel.classList.add('dragging');
        }

        if (hasDragged) {
          e.preventDefault();
          carousel.scrollLeft = scrollLeft - walk;
        }

        // Calculate velocity for momentum
        const now = Date.now();
        const dt = now - lastTime;
        if (dt > 0) {
          const currentX = e.pageX || e.touches?.[0]?.pageX || 0;
          velocity = (currentX - lastX) / dt;
          lastX = currentX;
          lastTime = now;
        }
      };

      const handleDragEnd = function () {
        if (!isDragging) return;
        isDragging = false;
        carousel.style.cursor = 'grab';

        // Apply momentum scrolling only if actually dragged
        if (hasDragged && Math.abs(velocity) > 0.1) {
          const momentum = function () {
            if (Math.abs(velocity) > 0.01) {
              carousel.scrollLeft -= velocity * 16; // 16ms per frame
              velocity *= 0.95; // Friction
              animationId = requestAnimationFrame(momentum);
            } else {
              velocity = 0;
              animationId = null;
            }
          };
          animationId = requestAnimationFrame(momentum);
        }

        // Remove dragging class after a short delay to prevent click events
        if (hasDragged) {
          setTimeout(() => {
            carousel.classList.remove('dragging');
          }, 100);
        }
      };

      // Mouse events
      carousel.addEventListener('mousedown', handleDragStart);
      carousel.addEventListener('mousemove', handleDragMove);
      carousel.addEventListener('mouseup', handleDragEnd);
      carousel.addEventListener('mouseleave', handleDragEnd);

      // Touch events
      carousel.addEventListener('touchstart', handleDragStart, { passive: true });
      carousel.addEventListener('touchmove', handleDragMove, { passive: false });
      carousel.addEventListener('touchend', handleDragEnd);

      // Prevent click events only when actually dragged
      carousel.addEventListener('click', function(e) {
        if (carousel.classList.contains('dragging')) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);
    }

  }

  function initUpsellsSizeStrips() {
    document.querySelectorAll('.cart-drawer__upsells-sizes').forEach(function (strip) {
      if (strip.hasAttribute('data-sizes-listener-attached')) return;
      strip.setAttribute('data-sizes-listener-attached', 'true');

      const list = strip.querySelector('.cart-drawer__upsells-sizes-list');
      const next = strip.querySelector('.cart-drawer__upsells-sizes-next');

      // Tapping a size adds that exact variant straight to the cart
      strip.querySelectorAll('.cart-drawer__upsells-size').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          const card = this.closest('.cart-drawer__upsells-link');
          addUpsellVariant(card, this.getAttribute('data-variant-id'));
        });
      });

      // Pager: advance one viewport of sizes, wrap back to the start at the end
      if (next && list) {
        next.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          const remaining = list.scrollWidth - list.clientWidth - list.scrollLeft;
          if (remaining > 2) {
            list.scrollBy({ left: list.clientWidth, behavior: 'smooth' });
          } else {
            list.scrollTo({ left: 0, behavior: 'smooth' });
          }
        });
      }

      // Only show the pager arrow when the sizes actually overflow
      if (list) {
        const checkOverflow = function () {
          strip.classList.toggle('has-overflow', list.scrollWidth > list.clientWidth + 1);
        };
        checkOverflow();
        setTimeout(checkOverflow, 300);
      }
    });
  }

  // NC-CRO 2026-10: size panel under the upsell carousel. Tapping a card (or its
  // "+") opens it with that product's sizes; the size from the cart is preselected
  // when the product has it in stock (data-preselect, rendered by Liquid).
  function closeUpsellPanel(panel) {
    if (!panel) return;
    panel.hidden = true;
    panel.removeAttribute('data-product-id');
    document.querySelectorAll('.cart-drawer__upsells-link.is-open').forEach(function (c) {
      c.classList.remove('is-open');
      const plus = c.querySelector('.cart-drawer__upsells-plus');
      if (plus) plus.setAttribute('aria-expanded', 'false');
    });
  }

  function openUpsellPanel(card) {
    const root = card.closest('.cart-drawer__upsells');
    const panel = root ? root.querySelector('.cart-drawer__upsells-panel') : null;
    if (!panel) return;

    const productId = card.getAttribute('data-product-id');
    if (!panel.hidden && panel.getAttribute('data-product-id') === productId) {
      closeUpsellPanel(panel);
      return;
    }
    closeUpsellPanel(panel);

    const sizesBox = panel.querySelector('.cart-drawer__upsells-panel-sizes');
    const addBtn = panel.querySelector('.cart-drawer__upsells-panel-add');
    const titleEl = panel.querySelector('.cart-drawer__upsells-panel-title');
    const priceEl = panel.querySelector('.cart-drawer__upsells-panel-price');
    const textPick = panel.getAttribute('data-text-pick') || '';
    const textAdd = panel.getAttribute('data-text-add') || '';
    let selected = null;

    if (titleEl) titleEl.textContent = card.getAttribute('data-title') || '';
    if (priceEl) priceEl.textContent = card.getAttribute('data-price') || '';

    const refreshAdd = function () {
      if (!addBtn) return;
      if (selected) {
        const price = selected.getAttribute('data-price') || card.getAttribute('data-price') || '';
        addBtn.disabled = false;
        addBtn.textContent = textAdd + ' · ' + selected.textContent.trim() + (price ? ' · ' + price : '');
        if (priceEl && price) priceEl.textContent = price;
      } else {
        addBtn.disabled = true;
        addBtn.textContent = textPick;
      }
    };

    sizesBox.innerHTML = '';
    const preselect = card.getAttribute('data-preselect') || '';
    card.querySelectorAll('.cart-drawer__upsells-size').forEach(function (src) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'cart-drawer__upsells-chip';
      chip.textContent = src.textContent.trim();
      chip.setAttribute('data-variant-id', src.getAttribute('data-variant-id') || '');
      chip.setAttribute('data-price', src.getAttribute('data-price') || '');
      chip.setAttribute('aria-pressed', 'false');
      if (src.getAttribute('aria-label')) chip.setAttribute('aria-label', src.getAttribute('aria-label'));
      if (src.disabled) chip.disabled = true;
      chip.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (chip.disabled) return;
        sizesBox.querySelectorAll('.cart-drawer__upsells-chip').forEach(function (c) {
          c.setAttribute('aria-pressed', 'false');
        });
        chip.setAttribute('aria-pressed', 'true');
        selected = chip;
        refreshAdd();
      });
      sizesBox.appendChild(chip);
      if (!src.disabled && preselect && chip.getAttribute('data-variant-id') === preselect) {
        chip.setAttribute('aria-pressed', 'true');
        selected = chip;
      }
    });
    refreshAdd();

    if (addBtn) {
      addBtn.onclick = function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (!selected) return;
        addUpsellVariant(card, selected.getAttribute('data-variant-id'), addBtn);
      };
    }

    panel.setAttribute('data-product-id', productId || '');
    panel.hidden = false;
    card.classList.add('is-open');
    const plus = card.querySelector('.cart-drawer__upsells-plus');
    if (plus) plus.setAttribute('aria-expanded', 'true');
    try {
      panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } catch (err) {}
  }

  function hideUpsellsInCart() {
    const cartProductIds = new Set();
    document.querySelectorAll('tbody[role="rowgroup"] tr[data-product-id]').forEach(function (row) {
      const productId = row.getAttribute('data-product-id');
      if (productId) cartProductIds.add(String(productId));
    });
    document.querySelectorAll('.cart-item[data-product-id]').forEach(function (item) {
      const productId = item.getAttribute('data-product-id');
      if (productId) cartProductIds.add(String(productId));
    });

    let visibleSlides = 0;
    document.querySelectorAll('.cart-drawer__upsells-slide').forEach(function (slide) {
      const link = slide.querySelector('.cart-drawer__upsells-link');
      const upsellProductId = link ? link.getAttribute('data-product-id') : null;
      if (upsellProductId && cartProductIds.has(String(upsellProductId))) {
        slide.style.display = 'none';
      } else {
        slide.style.display = '';
        visibleSlides++;
      }
    });

    // Hide the whole block (title included) when every product is already in the cart
    const upsellsContainer = document.querySelector('.cart-drawer__upsells');
    if (upsellsContainer) upsellsContainer.style.display = visibleSlides > 0 ? '' : 'none';

    if (typeof initUpsellsCarousel === 'function') setTimeout(initUpsellsCarousel, 50);
  }

  function reinitUpsells() {
    // Do NOT clear listener-attached flags. After a cart refresh the old nodes
    // are destroyed by innerHTML replacement, so new cards naturally have no
    // flag and get bound exactly once. Clearing flags on surviving nodes
    // caused duplicate click handlers and multiple add-to-cart requests.
    setTimeout(function () {
      initUpsellsCarousel();
      initUpsellsSizeStrips();
      initUpsellsAddToCart();
      hideUpsellsInCart();
    }, 50);
  }

  function initUpsellsAddToCart() {
    document.querySelectorAll('.cart-drawer__upsells-link[data-variant-id]').forEach(function (card) {
      if (card.hasAttribute('data-cart-listener-attached')) return;
      card.setAttribute('data-cart-listener-attached', 'true');

      card.addEventListener('click', function (e) {
        // Size buttons and the pager handle their own clicks (with stopPropagation);
        // this guard only protects against event-order edge cases.
        if (
          e.target &&
          e.target.closest &&
          (e.target.closest('.cart-drawer__upsells-size') || e.target.closest('.cart-drawer__upsells-sizes-next'))
        )
          return;
        e.preventDefault();
        e.stopPropagation();
        if (this.classList.contains('adding')) return;

        if (this.querySelector('.cart-drawer__upsells-sizes[data-multi]')) {
          // Multi-variant: open (or close) the size panel under the carousel.
          openUpsellPanel(this);
          return;
        }

        // Single variant: the whole card adds it directly
        addUpsellVariant(this, this.dataset.variantId);
      });
    });
  }

  function addUpsellVariant(card, variantId, statusEl) {
    if (!card || !variantId) return;
    // Guard against double-submission: ignore clicks while a request is in flight.
    if (card.classList.contains('adding')) return;

    // Localized state labels rendered by Liquid onto the upsells container
    const upsellsRoot = document.querySelector('.cart-drawer__upsells');
    const upsellTexts = (upsellsRoot && upsellsRoot.dataset) || {};
    const textAdding = upsellTexts.textAdding || (window.ncStrings && window.ncStrings.adding) || 'Añadiendo...';
    const textAdded = upsellTexts.textAdded || (window.ncStrings && window.ncStrings.added) || 'Añadido';

    // The strip label doubles as the status text while the request runs
    const addTextEl = statusEl || card.querySelector('.cart-drawer__upsells-sizes-label');
    if (addTextEl && !card.dataset.addTextDefault) {
      card.dataset.addTextDefault = addTextEl.textContent ? addTextEl.textContent.trim() : '';
    }

    card.classList.add('adding');
    const originalText = addTextEl ? addTextEl.textContent : '';
    if (addTextEl) addTextEl.textContent = textAdding;

    const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);
        // NC 2026-10: hidden line property (_ = not shown in cart/checkout) with the
        // cart-recs mode, so the weekly report can count what was bought from the recs
        const ncRecRoot = card.closest('.cart-drawer__upsells');
        formData.append('properties[_rec]', (ncRecRoot && ncRecRoot.getAttribute('data-nc-size-recs')) || 'liquid');

        const cartDrawer = document.querySelector('cart-drawer');
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        if (cartDrawer && cartDrawer.getSectionsToRender) {
          const sectionsToRender =
            cartDrawerItems && cartDrawerItems.getSectionsToRender
              ? cartDrawerItems.getSectionsToRender()
              : cartDrawer.getSectionsToRender();
          const sectionNames = sectionsToRender.map((section) => section.section || section.id);
          formData.append('sections', sectionNames.join(','));
          formData.append('sections_url', window.location.pathname);
          if (cartDrawer.setActiveElement) cartDrawer.setActiveElement(card);
        }

        const config =
          typeof fetchConfig !== 'undefined'
            ? fetchConfig('javascript')
            : { method: 'POST', headers: { Accept: 'application/javascript' } };
        config.headers = config.headers || {};
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

        fetch(cartAddUrl, config)
          .then((r) => {
            if (!r.ok) throw new Error('Network response was not ok');
            return r.json();
          })
          .then((resp) => {
            if (resp.status || resp.error) throw new Error(resp.error || resp.description || 'Add to cart failed');

            // GTM add_to_cart_minicart_cross push — disabled 2026-08: tracking
            // migrated to the official Google & YouTube app (web pixel).
            /*
            try {
              const addedVariantId = resp.id || resp.variant_id;
              const addedQuantity = resp.quantity || 1;
              const addedPrice = typeof resp.price === 'number'
                ? resp.price / 100
                : parseFloat(resp.price) || 0;
              const addedTitle = resp.product_title || resp.title || '';
              const shopCurrency =
                (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';

              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({
                event: 'add_to_cart_minicart_cross',
                product_id: addedVariantId,
                product_name: addedTitle,
                product_price: addedPrice,
                product_quantity: addedQuantity,
                currency: shopCurrency,
                value: addedPrice * addedQuantity,
              });
            } catch (e) {
              console.warn('add_to_cart_minicart_cross push failed', e);
            }
            */

            if (!cartDrawer) return;

            const softRefreshCartDrawer = () => {
              const cartUrl = window.routes?.cart_url || '/cart';
              const drawerSectionUrl = `${cartUrl}?section_id=cart-drawer`;
              const bubbleSectionUrl = `${cartUrl}?section_id=cart-icon-bubble`;

              window.__upsellsCartDrawerRefreshPromise = (window.__upsellsCartDrawerRefreshPromise || Promise.resolve())
                .catch(() => {})
                .then(() =>
                  Promise.all([
                    fetch(drawerSectionUrl).then((x) => x.text()),
                    fetch(bubbleSectionUrl).then((x) => x.text()),
                  ]).then(([drawerHtml, bubbleHtml]) => {
                    const parser = new DOMParser();
                    const drawerDoc = parser.parseFromString(drawerHtml, 'text/html');
                    const newRoot = drawerDoc.querySelector('#CartDrawer');
                    const currentRoot = document.getElementById('CartDrawer');
                    if (newRoot && currentRoot) currentRoot.innerHTML = newRoot.innerHTML;

                    const bubbleDoc = parser.parseFromString(bubbleHtml, 'text/html');
                    const newBubble = bubbleDoc.querySelector(
                      '#shopify-section-cart-icon-bubble, #cart-icon-bubble, .shopify-section'
                    );
                    const currentBubble = document.getElementById('cart-icon-bubble');
                    if (currentBubble && newBubble) currentBubble.innerHTML = newBubble.innerHTML;

                    const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
                    if (overlay && cartDrawer.close)
                      overlay.addEventListener('click', cartDrawer.close.bind(cartDrawer));

                    if (cartDrawer.open) cartDrawer.open();
                    reinitUpsells();
                  })
                );

              return window.__upsellsCartDrawerRefreshPromise;
            };

            softRefreshCartDrawer();

            if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'upsells', productVariantId: variantId });
            }
            document.dispatchEvent(new CustomEvent('cart:updated'));

            if (addTextEl) addTextEl.textContent = textAdded;
            setTimeout(() => {
              card.classList.remove('adding');
              card.classList.remove('upsell-selecting');
              if (addTextEl) addTextEl.textContent = card.dataset.addTextDefault || '';
            }, 1000);
          })
          .catch((err) => {
            console.error('Upsells add-to-cart error:', err);
            card.classList.remove('adding');
            if (addTextEl) addTextEl.textContent = originalText;
          });
  }

  function initialize() {
    updateFreeShippingProgress();
    setupSubtotalObserver();
    setupFooterObserver();
    startPolling();

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, handleCartUpdate);
    }
    document.addEventListener('cart:updated', handleCartUpdate);

    initUpsellsCarousel();
    initUpsellsSizeStrips();
    initUpsellsAddToCart();
    hideUpsellsInCart();

    window.reinitUpsells = reinitUpsells;

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && !cartDrawer.__upsellsHooked) {
      cartDrawer.__upsellsHooked = true;

      if (typeof cartDrawer.renderContents === 'function') {
        const originalRenderContents = cartDrawer.renderContents.bind(cartDrawer);
        cartDrawer.renderContents = function (...args) {
          const result = originalRenderContents(...args);
          setTimeout(() => {
            if (typeof window.reinitUpsells === 'function') window.reinitUpsells();
          }, 50);
          return result;
        };
      }

      if (typeof cartDrawer.open === 'function') {
        const originalOpen = cartDrawer.open.bind(cartDrawer);
        cartDrawer.open = function (...args) {
          const result = originalOpen(...args);
          setTimeout(() => {
            if (typeof window.reinitUpsells === 'function') window.reinitUpsells();
          }, 50);
          return result;
        };
      }

      const cartDrawerRoot = document.getElementById('CartDrawer');
      if (cartDrawerRoot && !cartDrawer.__upsellsObserver) {
        const obs = new MutationObserver(() => {
          clearTimeout(cartDrawer.__upsellsObsT);
          cartDrawer.__upsellsObsT = setTimeout(() => {
            if (typeof window.reinitUpsells === 'function') window.reinitUpsells();
          }, 80);
        });
        obs.observe(cartDrawerRoot, { childList: true, subtree: true });
        cartDrawer.__upsellsObserver = obs;
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  window.addEventListener('beforeunload', function () {
    stopPolling();
    if (animationFrame) cancelAnimationFrame(animationFrame);
    if (subtotalObserver) subtotalObserver.disconnect();
    if (footerObserver) footerObserver.disconnect();
    if (cartUpdateUnsubscriber) cartUpdateUnsubscriber();
    if (updateTimeout) clearTimeout(updateTimeout);
  });
})();
